import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/core/auth';
import { useSocket } from '@/contexts/SocketContext';
import { Button } from '@/ui/components/atoms/Button';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { TaskList } from './components/TaskList';
import { ChatModal } from '@/ui/components/molecules/ChatModal';
import { TaskCreationModal } from '@/ui/components/molecules/TaskCreationModal';
import { TaskAssignmentModal } from './components/TaskAssignmentModal';
import { KPICards } from './components/KPICards';
import { EmailContentModal } from '../emails/components/EmailContentModal';
import { EmailToAffaireModal } from './components/EmailToAffaireModal';
import { VoicemailToAffaireModal } from './components/VoicemailToAffaireModal';
import { tasksAPI } from './api/tasks.api';
import { emailsAPI } from '../tracker/api/emails.api';
import type { TrackerEmail } from '../tracker/types/tracker.types';
import { commentsApi } from './api/comments.api';
import { Task, User, WaitingStatus, UrgencyLevel } from './types/task.types';
import { useUnreadContext } from './contexts/UnreadContext';
import { useDebounce } from './hooks/useDebounce';
import { useURLStates } from './hooks/useURLState';
import { useTaskNotifications } from './hooks/useTaskNotifications';
import './styles/design-tokens.css';
import './styles/TasksLayout.css';
import './styles/TasksTab.css';

type ViewType = 'all' | 'mine' | 'team' | 'created' | 'unassigned';
type SegmentType = 'all' | 'unread' | 'done';
type SortType = 'updated_desc' | 'created_desc' | 'priority' | 'assignee';
type TaskViewMode = 'my_tasks' | 'user_tasks' | 'all_tasks';

interface URLState {
  view: ViewType;
  segment: SegmentType;
  q: string;
  sort: SortType;
  page: string;
  perPage: string;
  taskViewMode?: TaskViewMode;
  selectedUserId?: string;
  [key: string]: string | undefined;
}

const DEFAULT_URL_STATE: URLState = {
  view: 'mine',
  segment: 'all',
  q: '',
  sort: 'updated_desc',
  page: '1',
  perPage: '25',
  taskViewMode: 'my_tasks'
};

const SORT_OPTIONS = [
  { value: 'updated_desc', label: 'Dernière mise à jour (récent)' },
  { value: 'created_desc', label: 'Création (récent)' },
  { value: 'priority', label: 'Priorité' },
  { value: 'assignee', label: 'Assigné à' }
];

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

export const TasksTab: React.FC = () => {
  const { user } = useAuth();
  const { refetchUnreadCount } = useUnreadContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Enable task notifications (WebSocket events with sound)
  useTaskNotifications();
  
  // URL State Management
  const { values: urlState, setValue: setURLValue, setValues: setURLValues } = useURLStates<URLState>(
    DEFAULT_URL_STATE,
    { persistInLocalStorage: true, localStorageKey: 'mes-taches-filters' }
  );

  // Local State
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [assigningTask, setAssigningTask] = useState<Task | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [ownTasks, setOwnTasks] = useState<Task[]>([]);
  const [teamTasks, setTeamTasks] = useState<Task[]>([]);
  const [createdTasks, setCreatedTasks] = useState<Task[]>([]);
  const [unassignedTasks, setUnassignedTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedTaskIdForChat, setSelectedTaskIdForChat] = useState<string | null>(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.q || '');
  const [taskMessages, setTaskMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Email modal state (for viewing emails linked to tasks)
  const [viewEmailId, setViewEmailId] = useState<string | null>(null);
  const [viewingEmail, setViewingEmail] = useState<TrackerEmail | null>(null);
  const [_emailLoading, setEmailLoading] = useState(false);

  // EmailToAffaire modal state (for linking email to affaire when completing task)
  const [pendingDoneTask, setPendingDoneTask] = useState<Task | null>(null);
  const [emailForAffaire, setEmailForAffaire] = useState<TrackerEmail | null>(null);
  const [showEmailAffaireModal, setShowEmailAffaireModal] = useState(false);

  // VoicemailToAffaire modal state (tâche issue d'un message vocal :
  // lien affaire facultatif au moment de la validation)
  const [voicemailDoneTask, setVoicemailDoneTask] = useState<Task | null>(null);

  // New states for admin view modes
  const [taskViewMode, setTaskViewMode] = useState<TaskViewMode>(urlState.taskViewMode || 'my_tasks');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(urlState.selectedUserId || null);
  const [selectedUserTasks, setSelectedUserTasks] = useState<{
    ownTasks: Task[];
    teamTasks: Task[];
  }>({ ownTasks: [], teamTasks: [] });
  const [allSystemTasks, setAllSystemTasks] = useState<Task[]>([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userFilterText, setUserFilterText] = useState('');
  const [usersTaskStats, setUsersTaskStats] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    stats: {
      active_tasks: number;
      active_own_tasks: number;
      active_team_tasks: number;
      active_created_tasks: number;
      unread_tasks: number;
      urgent_tasks: number;
    }
  }>>([]);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  
  // Find selected task from tasks arrays - memoized to prevent unnecessary recalculations
  const selectedTaskForChat = React.useMemo(() => {
    if (!selectedTaskIdForChat) return null;

    // Search in all task arrays including createdTasks
    const allTasks = [...ownTasks, ...teamTasks, ...unassignedTasks, ...createdTasks];
    return allTasks.find(t => t.id === selectedTaskIdForChat) || null;
  }, [selectedTaskIdForChat, ownTasks, teamTasks, unassignedTasks, createdTasks]);
  
  // Debounce search
  const debouncedSearchTerm = useDebounce(localSearchTerm, 300);
  
  // Initialize localSearchTerm from URL on mount
  useEffect(() => {
    if (urlState.q !== undefined && urlState.q !== null) {
      setLocalSearchTerm(urlState.q);
    }
  }, []); // Only run once on mount

  // Update URL when debounced search changes
  useEffect(() => {
    // Only update URL if the debounced value is different
    if (debouncedSearchTerm !== urlState.q) {
      setURLValue('q', debouncedSearchTerm);
      setURLValue('page', '1'); // Reset to page 1 on search
    }
  }, [debouncedSearchTerm, setURLValue]);

  // WebSocket connection
  const { socket, isConnected: _isConnected } = useSocket();

  // Handle query parameters from navigation
  useEffect(() => {
    const taskIdParam = searchParams.get('taskId');

    if (taskIdParam) {
      // Open chat modal for the specific task
      setSelectedTaskIdForChat(taskIdParam);

      // Clean up URL params after handling
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('taskId');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams]);

  // Fetch data on mount and when view mode changes
  useEffect(() => {
    fetchUsers();
    fetchStats();
    if (isAdmin) {
      fetchUsersTaskStats();
    }
  }, [isAdmin]);
  
  // Track subscribed tasks for reconnection
  const subscribedTasksRef = useRef<Set<string>>(new Set());

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    if (!socket) return;
    
    // Re-subscribe to all previously subscribed tasks on reconnection
    const handleReconnect = () => {
      console.log('[TasksTab] WebSocket reconnected, re-subscribing to tasks...');
      subscribedTasksRef.current.forEach(taskId => {
        socket.emit('subscribe:task', taskId);
        console.log(`Re-subscribed to task: ${taskId}`);
      });
    };
    
    socket.on('connect', handleReconnect);
    
    const handleTaskUpdated = (_data: { task: Task }) => {
      // Refetch all tasks to ensure consistency across all users
      console.log('[TasksTab] Task updated, refetching tasks...');
      fetchTasks();
      fetchStats();
    };
    
    const handleTaskDeleted = (_data: { taskId: string }) => {
      // Refetch all tasks to ensure consistency across all users
      console.log('[TasksTab] Task deleted, refetching tasks...');
      fetchTasks();
      fetchStats();
    };
    
    const handleTaskCreated = () => {
      // Refetch tasks when a new task is created
      // The notification will be handled by useTaskNotifications hook
      fetchTasks();
      fetchStats();
    };
    
    const handleCommentAdded = (_data: {
      taskId: string;
      comment: any;
      timestamp: string;
    }) => {
      // Refetch tasks to update the list with new comment info
      console.log('[TasksTab] Comment added, refetching tasks...');
      fetchTasks();
    };

    const handleTaskCommentNotification = (_data: {
      taskId: string;
      comment: any;
      taskTitle: string;
    }) => {
      // Refetch tasks to update the list with new comment info
      console.log('[TasksTab] Task comment notification, refetching tasks...');
      fetchTasks();
    };
    
    // Listen to WebSocket events
    socket.on('task:updated', handleTaskUpdated);
    socket.on('task:deleted', handleTaskDeleted);
    socket.on('task:created', handleTaskCreated);
    socket.on('comment:added', handleCommentAdded);
    socket.on('task:comment:notification', handleTaskCommentNotification);
    
    return () => {
      socket.off('connect', handleReconnect);
      socket.off('task:updated', handleTaskUpdated);
      socket.off('task:deleted', handleTaskDeleted);
      socket.off('task:created', handleTaskCreated);
      socket.off('comment:added', handleCommentAdded);
      socket.off('task:comment:notification', handleTaskCommentNotification);
    };
  }, [socket]);

  // Handle Escape key to clear search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && localSearchTerm) {
        setLocalSearchTerm('');
        setURLValue('q', '');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [localSearchTerm]);

  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('[fetchTasks] Starting fetch for mode:', taskViewMode, 'selectedUserId:', selectedUserId);

      // Fetch based on current view mode
      if (taskViewMode === 'my_tasks') {
        console.log('[fetchTasks] Fetching my tasks...');
        // Fetch current user's tasks
        const response = await tasksAPI.getAllTasks();
        console.log('[fetchTasks] My tasks response:', response);
        if (response.success) {
          console.log('[fetchTasks] Setting tasks:', {
            ownTasks: response.data.ownTasks?.length || 0,
            teamTasks: response.data.teamTasks?.length || 0,
            unassignedTasks: response.data.unassignedTasks?.length || 0
          });
          setOwnTasks(response.data.ownTasks || []);
          setTeamTasks(response.data.teamTasks || []);
          setCreatedTasks(response.data.createdTasks || []);
          // Deduplicate unassigned tasks in case of duplicates from backend
          const uniqueUnassigned = response.data.unassignedTasks ?
            Array.from(new Map(response.data.unassignedTasks.map(task => [task.id, task])).values()) : [];
          setUnassignedTasks(uniqueUnassigned);
        }
      } else if (taskViewMode === 'user_tasks' && selectedUserId) {
        console.log('[fetchTasks] Fetching tasks for user:', selectedUserId);
        // Fetch selected user's tasks (admin only)
        const response = await tasksAPI.getTasksForUser(selectedUserId);
        console.log('[fetchTasks] User tasks response:', response);
        if (response.success) {
          setSelectedUserTasks({
            ownTasks: response.data.ownTasks,
            teamTasks: response.data.teamTasks
          });
        }
      } else if (taskViewMode === 'all_tasks') {
        console.log('[fetchTasks] Fetching all system tasks...');
        // Fetch all system tasks (admin only)
        const response = await tasksAPI.getAllSystemTasks();
        console.log('[fetchTasks] All system tasks response:', response);
        if (response.success && response.data) {
          console.log('All system tasks fetched:', response.data.length, 'tasks');
          setAllSystemTasks(response.data);
        }
      }
    } catch (error) {
      console.error('[fetchTasks] Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [taskViewMode, selectedUserId]);
  
  // Fetch tasks when view mode or selected user changes
  useEffect(() => {
    console.log('[TasksTab] View mode or user changed:', { taskViewMode, selectedUserId, isAdmin });
    if (isAdmin || taskViewMode === 'my_tasks') {
      fetchTasks();
    }
  }, [taskViewMode, selectedUserId, isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await tasksAPI.getAllUsers();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await tasksAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUsersTaskStats = async () => {
    if (!isAdmin) return;
    
    try {
      const response = await tasksAPI.getAllUsersTaskStats();
      if (response.success) {
        setUsersTaskStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching users task stats:', error);
    }
  };

  const handleMarkDone = async (taskId: string) => {
    const task = [...ownTasks, ...teamTasks, ...unassignedTasks, ...createdTasks].find(t => t.id === taskId);

    // Tâche issue d'un message vocal sans affaire : proposer de lier un
    // n° d'affaire au moment de la validation (facultatif)
    if (task?.source_voicemail_id && !task.k_id_affaire) {
      setVoicemailDoneTask(task);
      return; // Don't mark as done yet - wait for modal action
    }

    // Check if task has a linked email - if so, show the modal first
    if (task?.source_email_id) {
      try {
        // Fetch the email details for the modal
        const emailResponse = await emailsAPI.getEmailById(task.source_email_id);
        if (emailResponse.success && emailResponse.data) {
          setPendingDoneTask(task);
          setEmailForAffaire(emailResponse.data);
          setShowEmailAffaireModal(true);
          return; // Don't mark as done yet - wait for modal action
        }
      } catch (error) {
        console.error('Error fetching email for task:', error);
        // Fall through to normal flow if email fetch fails
      }
    }

    // Normal flow (no email or email fetch failed)
    await completeMarkDone(taskId);
  };

  const completeMarkDone = async (taskId: string, affaireId?: number | null) => {
    try {
      // Optimistic update: immediately update the UI
      const updateTaskInList = (tasks: Task[]) =>
        tasks.map(t => t.id === taskId
          ? { ...t, is_done: true, unread_count: 0, is_new: false }
          : t
        );

      setOwnTasks(prev => updateTaskInList(prev));
      setTeamTasks(prev => updateTaskInList(prev));
      setUnassignedTasks(prev => updateTaskInList(prev));

      // First mark the task as viewed to remove unread status
      const task = [...ownTasks, ...teamTasks, ...unassignedTasks].find(t => t.id === taskId);
      if (task && ((task.unread_count ?? 0) > 0 || task.is_new)) {
        await tasksAPI.markTaskViewed(taskId);
      }
      // Then mark the task as done (avec lien affaire facultatif)
      await tasksAPI.markTaskDone(taskId, affaireId ? { k_id_affaire: affaireId } : undefined);
      await fetchTasks();
      await fetchStats();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking task as done:', error);
      // Revert optimistic update on error
      await fetchTasks();
    }
  };

  // Handlers for EmailToAffaireModal
  const handleEmailAffaireSuccess = async () => {
    setShowEmailAffaireModal(false);
    if (pendingDoneTask) {
      await completeMarkDone(pendingDoneTask.id);
    }
    setPendingDoneTask(null);
    setEmailForAffaire(null);
  };

  const handleEmailAffaireSkip = async () => {
    setShowEmailAffaireModal(false);
    if (pendingDoneTask) {
      await completeMarkDone(pendingDoneTask.id);
    }
    setPendingDoneTask(null);
    setEmailForAffaire(null);
  };

  const handleUnmarkDone = async (taskId: string) => {
    try {
      setIsLoading(true);
      await tasksAPI.unmarkTaskDone(taskId);
      await fetchTasks();
      await fetchStats();
    } catch (error) {
      console.error('Error unmarking task as done:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: WaitingStatus) => {
    try {
      // Optimistic update: immediately update the UI
      const updateTaskInList = (tasks: Task[]) =>
        tasks.map(t => t.id === taskId
          ? { ...t, waiting_status: newStatus }
          : t
        );

      setOwnTasks(prev => updateTaskInList(prev));
      setTeamTasks(prev => updateTaskInList(prev));
      setUnassignedTasks(prev => updateTaskInList(prev));
      setCreatedTasks(prev => updateTaskInList(prev));

      // Call API to update status
      await tasksAPI.updateTaskStatus(taskId, newStatus);
      await fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      // Revert optimistic update on error
      await fetchTasks();
    }
  };

  const handleUrgencyChange = async (taskId: string, newUrgency: UrgencyLevel) => {
    try {
      // Optimistic update: immediately update the UI
      const updateTaskInList = (tasks: Task[]) =>
        tasks.map(t => t.id === taskId
          ? { ...t, criticity: newUrgency }
          : t
        );

      setOwnTasks(prev => updateTaskInList(prev));
      setTeamTasks(prev => updateTaskInList(prev));
      setUnassignedTasks(prev => updateTaskInList(prev));
      setCreatedTasks(prev => updateTaskInList(prev));

      // Call API to update urgency
      await tasksAPI.updateTaskUrgency(taskId, newUrgency);
      await fetchTasks();
    } catch (error) {
      console.error('Error updating task urgency:', error);
      // Revert optimistic update on error
      await fetchTasks();
    }
  };

  const handleMarkUnread = async (taskId: string) => {
    try {
      setIsLoading(true);
      await tasksAPI.markTaskUnread(taskId);
      await fetchTasks();
      await fetchStats();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking task as unread:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const handleAssignTask = (task: Task) => {
    setAssigningTask(task);
    setIsAssignmentModalOpen(true);
  };

  const handleAssignment = async (taskId: string, userId: string | null, roleName: string | null) => {
    try {
      const assignmentData: { assignedToUsers?: string[]; assignedToRoles?: string[] } = {};

      if (userId) {
        assignmentData.assignedToUsers = [userId];
      }

      if (roleName) {
        assignmentData.assignedToRoles = [roleName];
      }

      await tasksAPI.assignTask(taskId, assignmentData);
      await fetchTasks();
      await fetchStats();
      // Remove the task from unassigned after successful assignment
      setUnassignedTasks(prev => prev.filter(t => t.id !== taskId));
      setIsAssignmentModalOpen(false);
      setAssigningTask(null);
    } catch (error) {
      console.error('Error assigning task:', error);
      throw error;
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      setIsLoading(true);
      await tasksAPI.deleteTask(taskId);
      await fetchTasks();
      await fetchStats();
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load messages for selected task
  const loadTaskMessages = useCallback(async () => {
    if (!selectedTaskIdForChat) return;
    
    setMessagesLoading(true);
    try {
      const response = await commentsApi.getTaskComments(selectedTaskIdForChat);
      if (response.success) {
        // Transform comments to match ChatModal message format
        const messages = response.data.map((comment: any) => ({
          id: comment.id,
          text: comment.message,
          author: {
            id: comment.user_id,
            name: comment.user_name || 'Utilisateur',
            initials: comment.user_name ? comment.user_name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U'
          },
          created_at: comment.created_at,
          is_own: comment.user_id === user?.id
        }));
        setTaskMessages(messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedTaskIdForChat, user?.id]);

  // Send message handler
  const handleSendMessage = useCallback(async (message: string) => {
    if (!selectedTaskIdForChat) return;

    try {
      const response = await commentsApi.addComment(selectedTaskIdForChat, message);
      if (response.success) {
        // Add new message to list
        const newMessage = {
          id: response.data.id,
          text: response.data.message,
          author: {
            id: user?.id || '',
            name: user ? `${user.firstName} ${user.lastName}` : 'Vous',
            initials: user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : 'V'
          },
          created_at: response.data.created_at,
          is_own: true
        };
        setTaskMessages(prev => [...prev, newMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [selectedTaskIdForChat, user]);

  // Mark task as viewed
  const markTaskAsViewed = useCallback(async () => {
    if (!selectedTaskIdForChat) return;

    try {
      await tasksAPI.markTaskViewed(selectedTaskIdForChat);
      // Update local state instead of refetching all tasks
      const updateTaskAsViewed = (tasks: Task[]) =>
        tasks.map(task =>
          task.id === selectedTaskIdForChat
            ? { ...task, is_new: false, unread_count: 0 }
            : task
        );

      setOwnTasks(prev => updateTaskAsViewed(prev));
      setTeamTasks(prev => updateTaskAsViewed(prev));
      setCreatedTasks(prev => updateTaskAsViewed(prev));
      setUnassignedTasks(prev => updateTaskAsViewed(prev));

      refetchUnreadCount();
      // Removed fetchTasks() to prevent full page reload
    } catch (error) {
      console.error('Error marking task as viewed:', error);
    }
  }, [selectedTaskIdForChat, refetchUnreadCount]);

  // Load messages when task is selected for chat
  useEffect(() => {
    if (selectedTaskIdForChat) {
      loadTaskMessages();
      markTaskAsViewed();
      // Subscribe to task-specific room for real-time comments
      if (socket) {
        socket.emit('subscribe:task', selectedTaskIdForChat);
        subscribedTasksRef.current.add(selectedTaskIdForChat);
        console.log(`Subscribed to task: ${selectedTaskIdForChat}`);
      }
    } else {
      // Unsubscribe when closing modal
      if (socket && selectedTaskIdForChat) {
        socket.emit('unsubscribe:task', selectedTaskIdForChat);
        subscribedTasksRef.current.delete(selectedTaskIdForChat);
        console.log(`Unsubscribed from task: ${selectedTaskIdForChat}`);
      }
      setTaskMessages([]);
      // Unsubscribe from previous task
      if (socket && selectedTaskIdForChat) {
        socket.emit('unsubscribe:task', selectedTaskIdForChat);
      }
    }

    // Cleanup: unsubscribe when component unmounts or task changes
    return () => {
      if (socket && selectedTaskIdForChat) {
        socket.emit('unsubscribe:task', selectedTaskIdForChat);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskIdForChat, socket]);
  
  // Subscribe to real-time comment updates for the chat
  useEffect(() => {
    if (!socket || !selectedTaskIdForChat) return;
    
    const handleCommentAdded = (data: {
      taskId: string;
      comment: any;
      timestamp: string;
    }) => {
      // Only update if it's for the currently open chat
      if (data.taskId === selectedTaskIdForChat && data.comment.user_id !== user?.id) {
        const newMessage = {
          id: data.comment.id,
          text: data.comment.message,
          author: {
            id: data.comment.user_id,
            name: data.comment.user_name || 'Unknown',
            initials: data.comment.user_name ? 
              data.comment.user_name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 
              'U'
          },
          created_at: data.comment.created_at,
          is_own: false
        };
        setTaskMessages(prev => {
          // Check if message already exists to avoid duplicates
          if (prev.some(m => m.id === newMessage.id)) {
            return prev;
          }
          return [...prev, newMessage];
        });
      }
    };
    
    socket.on('comment:added', handleCommentAdded);
    
    return () => {
      socket.off('comment:added', handleCommentAdded);
    };
  }, [socket, selectedTaskIdForChat, user?.id]);

  // Fetch email when viewEmailId changes (for email-linked tasks)
  useEffect(() => {
    const fetchEmail = async () => {
      if (!viewEmailId) {
        setViewingEmail(null);
        return;
      }
      setEmailLoading(true);
      try {
        const response = await emailsAPI.getEmailById(viewEmailId);
        if (response.success && response.data) {
          setViewingEmail(response.data);
        }
      } catch (error) {
        console.error('Error fetching email:', error);
        alert('Erreur lors du chargement de l\'email');
        setViewEmailId(null);
      } finally {
        setEmailLoading(false);
      }
    };
    fetchEmail();
  }, [viewEmailId]);

  // Sort tasks based on selected sort option
  const sortTasks = useCallback((tasks: Task[]) => {
    // First, separate tasks into done and not done groups
    const activeTasks = tasks.filter(task => !task.is_done);
    const completedTasks = tasks.filter(task => task.is_done);

    // Within active tasks, separate into unread and read groups
    // A task is considered unread if it has unread_count > 0 OR is_new is true
    const unreadActiveTasks = activeTasks.filter(task => {
      // Convert unread_count to number if it's a string
      const unreadCount = typeof task.unread_count === 'string'
        ? parseInt(task.unread_count, 10)
        : (task.unread_count ?? 0);
      const hasUnread = unreadCount > 0;
      const isNew = task.is_new === true;
      return hasUnread || isNew;
    });
    const readActiveTasks = activeTasks.filter(task => {
      // Convert unread_count to number if it's a string
      const unreadCount = typeof task.unread_count === 'string'
        ? parseInt(task.unread_count, 10)
        : (task.unread_count ?? 0);
      const hasUnread = unreadCount > 0;
      const isNew = task.is_new === true;
      return !(hasUnread || isNew);
    });

    // Debug sorting - log details about unread detection
    console.log('[Sorting Debug] Total active tasks:', activeTasks.length);
    console.log('[Sorting Debug] Unread active tasks:', unreadActiveTasks.length);
    console.log('[Sorting Debug] Read active tasks:', readActiveTasks.length);
    if (unreadActiveTasks.length > 0) {
      console.log('[Sorting Debug] First unread task:', {
        id: unreadActiveTasks[0].id,
        message: unreadActiveTasks[0].message.substring(0, 50),
        unread_count: unreadActiveTasks[0].unread_count,
        is_new: unreadActiveTasks[0].is_new
      });
    }

    // Within completed tasks, separate into unread and read groups
    const unreadCompletedTasks = completedTasks.filter(task => {
      // Convert unread_count to number if it's a string
      const unreadCount = typeof task.unread_count === 'string'
        ? parseInt(task.unread_count, 10)
        : (task.unread_count ?? 0);
      const hasUnread = unreadCount > 0;
      const isNew = task.is_new === true;
      return hasUnread || isNew;
    });
    const readCompletedTasks = completedTasks.filter(task => {
      // Convert unread_count to number if it's a string
      const unreadCount = typeof task.unread_count === 'string'
        ? parseInt(task.unread_count, 10)
        : (task.unread_count ?? 0);
      const hasUnread = unreadCount > 0;
      const isNew = task.is_new === true;
      return !(hasUnread || isNew);
    });
    
    // Sort function based on selected criteria
    const sortBySelectedCriteria = (taskList: Task[]) => {
      const sorted = [...taskList];
      switch (urlState.sort) {
        case 'updated_desc':
          return sorted.sort((a, b) => {
            // Use last_updated_at if available, otherwise fall back to created_at
            const dateA = a.last_updated_at || a.created_at;
            const dateB = b.last_updated_at || b.created_at;
            return new Date(dateB).getTime() - new Date(dateA).getTime();
          });
        case 'priority': {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return sorted.sort((a, b) => priorityOrder[a.criticity] - priorityOrder[b.criticity]);
        }
        case 'assignee':
          return sorted.sort((a, b) => {
            const nameA = a.assigned_user_names?.[0] || '';
            const nameB = b.assigned_user_names?.[0] || '';
            return nameA.localeCompare(nameB);
          });
        case 'created_desc':
        default:
          return sorted.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
      }
    };
    
    // Sort each group independently
    const sortedUnreadActiveTasks = sortBySelectedCriteria(unreadActiveTasks);
    const sortedReadActiveTasks = sortBySelectedCriteria(readActiveTasks);
    const sortedUnreadCompletedTasks = sortBySelectedCriteria(unreadCompletedTasks);
    const sortedReadCompletedTasks = sortBySelectedCriteria(readCompletedTasks);
    
    // Combine: active tasks first (unread then read), then completed tasks (unread then read)
    const finalSorted = [
      ...sortedUnreadActiveTasks,
      ...sortedReadActiveTasks,
      ...sortedUnreadCompletedTasks,
      ...sortedReadCompletedTasks
    ];

    // Debug final sorting result
    console.log('[Sorting Debug] Final sorted array length:', finalSorted.length);
    if (finalSorted.length > 0) {
      console.log('[Sorting Debug] First 3 tasks in final sorted array:',
        finalSorted.slice(0, 3).map(t => ({
          message: t.message.substring(0, 30),
          unread_count: t.unread_count,
          is_new: t.is_new
        }))
      );
    }

    return finalSorted;
  }, [urlState.sort]);

  // Get filtered and sorted task list
  const getFilteredTasks = useMemo(() => {
    let tasks: Task[] = [];

    // Determine which task set to use based on view mode
    let currentOwnTasks = ownTasks;
    let currentTeamTasks = teamTasks;
    let currentUnassignedTasks = unassignedTasks;

    console.log('Current view mode:', taskViewMode);
    console.log('All system tasks length:', allSystemTasks.length);

    if (taskViewMode === 'user_tasks' && selectedUserId) {
      currentOwnTasks = selectedUserTasks.ownTasks;
      currentTeamTasks = selectedUserTasks.teamTasks;
      currentUnassignedTasks = []; // User view doesn't show unassigned
      console.log('Using selected user tasks:', selectedUserTasks);
    } else if (taskViewMode === 'all_tasks') {
      tasks = allSystemTasks;
      console.log('Using all system tasks:', tasks.length);
      // Skip the view filtering for all_tasks mode
    }

    // First filter by view (KPI card selection) - skip if all_tasks mode
    if (taskViewMode !== 'all_tasks') {
      switch (urlState.view) {
        case 'all': {
          // "Toutes mes tâches" - includes own, team, and created tasks, excludes unassigned
          const allTasks = [...currentOwnTasks, ...currentTeamTasks, ...createdTasks].filter(t =>
            !t.needs_assignment
          );
          // Remove duplicates (a task could be both created and assigned to the user)
          tasks = Array.from(
            new Map(allTasks.map(task => [task.id, task])).values()
          );
          break;
        }
        case 'team':
          // Only role-based tasks, exclude unassigned
          tasks = [...currentTeamTasks].filter(t =>
            !t.needs_assignment
          );
          break;
        case 'created':
          // Tasks created by the user
          tasks = [...createdTasks];
          break;
        case 'unassigned':
          console.log('[View Filter] Unassigned tasks:', currentUnassignedTasks.length, currentUnassignedTasks);
          // Only show tasks that need assignment (already exclusive)
          tasks = [...currentUnassignedTasks];
          break;
        case 'mine':
        default:
          // Only explicitly assigned tasks to the user
          tasks = [...currentOwnTasks].filter(t =>
            !t.needs_assignment
          );
          break;
      }
    }

    // Then apply segment filter
    switch (urlState.segment) {
      case 'unread':
        // Filter to show only unread tasks (tasks with unread_count > 0 or is_new = true)
        tasks = tasks.filter(task => {
          const unreadCount = typeof task.unread_count === 'string'
            ? parseInt(task.unread_count, 10)
            : (task.unread_count ?? 0);
          return (unreadCount > 0) || task.is_new === true;
        });
        break;
      case 'done':
        tasks = tasks.filter(task => task.is_done);
        break;
    }

    // Apply search filter
    if (urlState.q) {
      const searchLower = urlState.q.toLowerCase();
      tasks = tasks.filter(task => {
        return (
          task.message.toLowerCase().includes(searchLower) ||
          task.created_by_name?.toLowerCase().includes(searchLower) ||
          task.assigned_user_names?.some(name => name.toLowerCase().includes(searchLower))
        );
      });
    }

    // Filter out tasks that are linked to an affaire (those are managed in tracker)
    tasks = tasks.filter(task => !task.k_id_affaire);

    // Sort tasks
    return sortTasks(tasks);
  }, [ownTasks, teamTasks, unassignedTasks, urlState.view, urlState.segment, urlState.q, urlState.sort, user?.id, sortTasks, taskViewMode, selectedUserId, selectedUserTasks, allSystemTasks]);

  // Pagination
  const currentPage = parseInt(urlState.page, 10);
  const itemsPerPage = parseInt(urlState.perPage, 10);
  const totalPages = Math.ceil(getFilteredTasks.length / itemsPerPage);
  const paginatedTasks = getFilteredTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Debug pagination
  console.log(`[Pagination Debug - IMPORTANT] Page ${currentPage}, showing items ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, getFilteredTasks.length)} of ${getFilteredTasks.length}`);
  if (paginatedTasks.length > 0) {
    console.log('[Pagination Debug] Tasks on this page:', paginatedTasks.map(t => ({
      message: t.message.substring(0, 30),
      unread_count: t.unread_count,
      unread_count_type: typeof t.unread_count,
      unread_count_parsed: parseInt(String(t.unread_count ?? 0), 10),
      is_new: t.is_new,
      has_unread: (parseInt(String(t.unread_count ?? 0), 10) > 0) || t.is_new
    })));

    // Check if there are unread tasks on page 2 or later
    if (currentPage >= 2) {
      const unreadOnThisPage = paginatedTasks.filter(t =>
        (parseInt(String(t.unread_count ?? 0), 10) > 0) || t.is_new
      );
      if (unreadOnThisPage.length > 0) {
        console.warn(`[SORTING BUG] Found ${unreadOnThisPage.length} unread tasks on page ${currentPage}! These should be on page 1.`);
        console.warn('[SORTING BUG] Unread tasks on this page:', unreadOnThisPage.map(t => ({
          id: t.id,
          message: t.message.substring(0, 50),
          unread_count: t.unread_count,
          is_new: t.is_new
        })));
      }
    }
  }

  // Calculate stats for KPI cards based on current view mode
  // Filter out tasks linked to an affaire (k_id_affaire) as those are managed in tracker
  const kpiStats = useMemo(() => {
    // Helper to filter out affaire-linked tasks
    const excludeAffaireTasks = (tasks: Task[]) => tasks.filter(t => !t.k_id_affaire);

    if (taskViewMode === 'all_tasks') {
      // For all tasks view, show stats from all system tasks (exclude unassigned from 'all' count)
      // Also exclude completed tasks and affaire-linked tasks from all counts
      const filteredAllTasks = excludeAffaireTasks(allSystemTasks);
      return {
        all: filteredAllTasks.filter(t => !t.is_done && t.needs_assignment !== true).length,
        mine: filteredAllTasks.filter(t => !t.is_done && t.assigned_user_ids?.includes(user?.id || '')).length,
        team: filteredAllTasks.filter(t => !t.is_done && (t.assigned_roles?.length ?? 0) > 0).length,
        created: 0, // Not available in all_tasks mode
        unassigned: filteredAllTasks.filter(t => !t.is_done && t.needs_assignment === true).length
      };
    } else if (taskViewMode === 'user_tasks' && selectedUserId) {
      // For user tasks view, show stats from selected user's tasks
      // Exclude completed tasks and affaire-linked tasks
      const activeOwnTasks = excludeAffaireTasks(selectedUserTasks.ownTasks).filter(t => !t.is_done);
      const activeTeamTasks = excludeAffaireTasks(selectedUserTasks.teamTasks).filter(t => !t.is_done);
      const allUserTasks = [...activeOwnTasks, ...activeTeamTasks];
      const uniqueTasks = Array.from(new Map(allUserTasks.map(t => [t.id, t])).values());
      return {
        all: uniqueTasks.length,
        mine: activeOwnTasks.length,
        team: activeTeamTasks.length,
        created: 0, // Not available in user view
        unassigned: 0 // User view doesn't show unassigned tasks
      };
    } else {
      // For my tasks view, show current user's stats
      // Filter out unassigned, completed, and affaire-linked tasks from counts to match the view filters
      const filteredOwnTasks = excludeAffaireTasks(ownTasks).filter(t => !t.is_done && !t.needs_assignment);
      const filteredTeamTasks = excludeAffaireTasks(teamTasks).filter(t => !t.is_done && !t.needs_assignment);
      const filteredCreatedTasks = excludeAffaireTasks(createdTasks).filter(t => !t.is_done);
      const filteredUnassignedTasks = excludeAffaireTasks(unassignedTasks).filter(t => !t.is_done);

      // For 'all', combine own, team, and created tasks (with deduplication)
      const allTaskIds = new Set([...filteredOwnTasks, ...filteredTeamTasks, ...filteredCreatedTasks].map(t => t.id));

      return {
        all: allTaskIds.size,
        mine: filteredOwnTasks.length,
        team: filteredTeamTasks.length,
        created: filteredCreatedTasks.length,
        unassigned: filteredUnassignedTasks.length
      };
    }
  }, [taskViewMode, allSystemTasks, selectedUserTasks, ownTasks, teamTasks, createdTasks, unassignedTasks, stats, user?.id, selectedUserId]);

  // Calculate unread counts for each view
  // Filter out tasks linked to an affaire (k_id_affaire) as those are managed in tracker
  const unreadCounts = useMemo(() => {
    // Helper to filter out affaire-linked tasks
    const excludeAffaireTasks = (tasks: Task[]) => tasks.filter(t => !t.k_id_affaire);

    const calculateUnread = (tasks: Task[]) => {
      return excludeAffaireTasks(tasks).filter(t => !t.is_done && ((t.unread_count ?? 0) > 0 || t.is_new)).length;
    };

    const ownUnread = calculateUnread(ownTasks);
    const teamUnread = calculateUnread(teamTasks);
    const createdUnread = calculateUnread(createdTasks);

    // For 'all', combine unique tasks with unread messages (excluding affaire-linked tasks)
    const allTasksWithUnread = excludeAffaireTasks([...ownTasks, ...teamTasks, ...createdTasks])
      .filter(t => !t.is_done && ((t.unread_count ?? 0) > 0 || t.is_new));
    const uniqueUnreadIds = new Set(allTasksWithUnread.map(t => t.id));

    return {
      all: uniqueUnreadIds.size,
      mine: ownUnread,
      team: teamUnread,
      created: createdUnread,
      unassigned: calculateUnread(unassignedTasks)
    };
  }, [ownTasks, teamTasks, createdTasks, unassignedTasks]);

  const getEmptyMessage = () => {
    if (urlState.q) {
      return `Aucun résultat pour « ${urlState.q} »`;
    }

    switch (urlState.view) {
      case 'all':
        return 'Aucune tâche pour le moment';
      case 'team':
        return 'Aucune tâche assignée à votre équipe';
      case 'created':
        return 'Aucune tâche créée par vous';
      case 'unassigned':
        return 'Aucune tâche à assigner';
      case 'mine':
      default:
        return 'Aucune tâche qui vous est assignée';
    }
  };

  const getTabTitle = () => {
    switch (urlState.view) {
      case 'all':
        return 'Toutes mes tâches';
      case 'team':
        return 'Tâches équipe';
      case 'created':
        return 'Tâches créées';
      case 'unassigned':
        return 'À assigner';
      case 'mine':
      default:
        return 'Mes tâches';
    }
  };

  return (
    <div className="mes-taches-layout">
      <div className="mes-taches-header">
        <div>
          <h1 className="mes-taches-title text-h1">Gestion des Tâches</h1>
          <p className="mes-taches-subtitle text-body-muted">Gérez vos tâches personnelles et d'équipe</p>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditingTask(null);
            setIsTaskModalOpen(true);
          }}
          className="mes-taches-btn-new-task focus-ring"
        >
          <PlusIcon className="mes-taches-btn-icon" />
          <span>Nouvelle tâche</span>
        </Button>
      </div>

      {/* Admin View Mode Switcher - Only visible for admins */}
      {isAdmin && (
        <div className="mes-taches-view-switcher">
          <div className="mes-taches-view-buttons">
            <button
              className={`mes-taches-view-btn ${taskViewMode === 'my_tasks' ? 'active' : ''}`}
              onClick={() => {
                setTaskViewMode('my_tasks');
                setSelectedUserId(null);
              }}
            >
              Mes tâches
            </button>
            <button
              className={`mes-taches-view-btn ${taskViewMode === 'user_tasks' ? 'active' : ''}`}
              onClick={() => {
                setTaskViewMode('user_tasks');
                setShowUserSelector(true);
              }}
            >
              Tâches d'un utilisateur
            </button>
            <button
              className={`mes-taches-view-btn ${taskViewMode === 'all_tasks' ? 'active' : ''}`}
              onClick={() => {
                setTaskViewMode('all_tasks');
                setSelectedUserId(null);
              }}
            >
              Toutes les tâches système
            </button>
          </div>
          
          {/* User Selector for user_tasks mode */}
          {taskViewMode === 'user_tasks' && (
            <div className="mes-taches-user-selector-wrapper">
              <div className="mes-taches-user-selector-content">
                <label className="mes-taches-user-selector-label">
                  Utilisateur sélectionné:
                </label>
                <div className="mes-taches-user-selector-container">
                  <button
                    className="mes-taches-user-selector-button"
                    onClick={() => {
                      setShowUserSelector(!showUserSelector);
                      if (!showUserSelector) {
                        setUserFilterText('');
                      }
                    }}
                  >
                    <div className="mes-taches-selected-user-display">
                      {selectedUserId ? (
                        <>
                          <div className="mes-taches-user-avatar">
                            {users.find(u => u.id === selectedUserId)?.first_name?.[0]}
                            {users.find(u => u.id === selectedUserId)?.last_name?.[0]}
                          </div>
                          <div className="mes-taches-selected-user-info-wrapper">
                            <div className="mes-taches-selected-user-name">
                              {users.find(u => u.id === selectedUserId)?.first_name} {users.find(u => u.id === selectedUserId)?.last_name}
                            </div>
                            {(() => {
                              const userStats = usersTaskStats.find(u => u.id === selectedUserId);
                              return userStats ? (
                                <div className="mes-taches-selected-user-stats">
                                  <div className="mes-taches-selected-user-stat">
                                    <span>Actives:</span>
                                    <span className="count">{userStats.stats.active_tasks}</span>
                                  </div>
                                  <div className="mes-taches-selected-user-stat">
                                    <span>Non lues:</span>
                                    <span className="count">{userStats.stats.unread_tasks}</span>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>
                          Sélectionner un utilisateur...
                        </span>
                      )}
                    </div>
                    <ChevronDownIcon className="mes-taches-user-selector-chevron" />
                  </button>
                  
                  {showUserSelector && (
                    <div className="mes-taches-user-dropdown">
                      <div className="mes-taches-user-filter-container">
                        <input
                          type="text"
                          className="mes-taches-user-filter-input"
                          placeholder="Filtrer par nom..."
                          value={userFilterText}
                          onChange={(e) => setUserFilterText(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                        {userFilterText && (
                          <button
                            className="mes-taches-user-filter-clear"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUserFilterText('');
                            }}
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {['admin', 'bureau', 'expert'].map(role => {
                        const roleUsers = users.filter(u => {
                          if (u.role !== role) return false;
                          if (!userFilterText) return true;
                          const searchTerm = userFilterText.toLowerCase();
                          const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
                          const email = u.email?.toLowerCase() || '';
                          return fullName.includes(searchTerm) || email.includes(searchTerm);
                        });
                        if (roleUsers.length === 0) return null;
                        
                        return (
                          <div key={role} className="mes-taches-user-group">
                            <div className="mes-taches-user-group-header">
                              {role === 'admin' ? 'Administrateurs' : 
                               role === 'bureau' ? 'Bureau' : 'Experts'}
                            </div>
                            {roleUsers.map(user => {
                              const userStats = usersTaskStats.find(u => u.id === user.id);
                              return (
                                <button
                                  key={user.id}
                                  className={`mes-taches-user-item ${selectedUserId === user.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedUserId(user.id);
                                    setShowUserSelector(false);
                                    setUserFilterText('');
                                  }}
                                >
                                  <div className="mes-taches-user-info-left">
                                    <div className="mes-taches-user-name">
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className="mes-taches-user-role-label">
                                      {user.email}
                                    </div>
                                  </div>
                                  {userStats ? (
                                    <div className="mes-taches-user-stats">
                                      {userStats.stats.active_tasks > 0 && (
                                        <div className="mes-taches-user-stat active">
                                          <span className="mes-taches-user-stat-value">{userStats.stats.active_tasks}</span>
                                          <span className="mes-taches-user-stat-label">actives</span>
                                        </div>
                                      )}
                                      {userStats.stats.unread_tasks > 0 && (
                                        <div className="mes-taches-user-stat unread">
                                          <span className="mes-taches-user-stat-value">{userStats.stats.unread_tasks}</span>
                                          <span className="mes-taches-user-stat-label">non lues</span>
                                        </div>
                                      )}
                                      {userStats.stats.active_tasks === 0 && userStats.stats.unread_tasks === 0 && (
                                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                          Aucune tâche
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                      Chargement...
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {selectedUserId && (
                <p className="mes-taches-selected-user-info">
                  Affichage des tâches de {users.find(u => u.id === selectedUserId)?.first_name} {users.find(u => u.id === selectedUserId)?.last_name}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <KPICards
        activeView={urlState.view}
        stats={kpiStats}
        unreadCounts={unreadCounts}
        onViewChange={(view) => {
          setURLValues({ view, page: '1' });
        }}
        isLoading={statsLoading}
        userRole={user?.role}
      />

          {/* Search, Filters, and Sort */}
          <div className="mes-taches-controls">
            <div className="mes-taches-search-wrapper">
              <MagnifyingGlassIcon className="mes-taches-search-icon" />
              <input
                type="text"
                className="mes-taches-search-input focus-ring"
                placeholder="Rechercher une tâche par titre, assignée ou date..."
                value={localSearchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setLocalSearchTerm(newValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setURLValue('q', localSearchTerm);
                  }
                }}
              />
              {localSearchTerm && (
                <button
                  className="mes-taches-search-clear"
                  onClick={() => {
                    setLocalSearchTerm('');
                    setURLValue('q', '');
                  }}
                  aria-label="Effacer la recherche"
                >
                  <XMarkIcon className="mes-taches-clear-icon" />
                </button>
              )}
            </div>
            
            <div className="mes-taches-filter-buttons">
              <button
                className={`mes-taches-filter-btn ${urlState.segment === 'all' ? 'active' : ''}`}
                onClick={() => setURLValue('segment', 'all')}
                aria-pressed={urlState.segment === 'all'}
              >
                Toutes
              </button>
              <button
                className={`mes-taches-filter-btn ${urlState.segment === 'unread' ? 'active' : ''}`}
                onClick={() => setURLValue('segment', 'unread')}
                aria-pressed={urlState.segment === 'unread'}
              >
                Non lu
              </button>
              <button
                className={`mes-taches-filter-btn ${urlState.segment === 'done' ? 'active' : ''}`}
                onClick={() => setURLValue('segment', 'done')}
                aria-pressed={urlState.segment === 'done'}
              >
                Terminées
              </button>
            </div>

            <div className="mes-taches-controls-right">
              {/* Sort Dropdown */}
              <div className="mes-taches-sort-dropdown">
                <button
                  className="mes-taches-sort-btn focus-ring"
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  aria-expanded={showSortDropdown}
                  aria-haspopup="listbox"
                >
                  <span>Trier</span>
                  <ChevronDownIcon className="mes-taches-sort-icon" />
                </button>
                {showSortDropdown && (
                  <>
                    <div 
                      className="mes-taches-dropdown-overlay" 
                      onClick={() => setShowSortDropdown(false)}
                    />
                    <div className="mes-taches-dropdown-menu" role="listbox">
                      {SORT_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          className={`mes-taches-dropdown-item ${urlState.sort === option.value ? 'active' : ''}`}
                          onClick={() => {
                            setURLValue('sort', option.value as SortType);
                            setShowSortDropdown(false);
                          }}
                          role="option"
                          aria-selected={urlState.sort === option.value}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

      {/* Task List */}
      <TaskList
        key={`${urlState.view}-${urlState.segment}-${currentPage}`}
        tasks={paginatedTasks}
        title={getTabTitle()}
        emptyMessage={getEmptyMessage()}
        onMarkDone={handleMarkDone}
        onUnmarkDone={handleUnmarkDone}
        onMarkUnread={handleMarkUnread}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onAssign={handleAssignTask}
        onViewEmail={(emailId) => setViewEmailId(emailId)}
        onOpenChat={(task: Task) => {
          setSelectedTaskIdForChat(task.id);
        }}
        onStatusChange={handleStatusChange}
        onUrgencyChange={handleUrgencyChange}
        isLoading={isLoading}
        currentUserId={user?.id || ''}
        showCreateButton={!urlState.q && urlState.segment === 'all'}
        onCreateTask={() => {
          setEditingTask(null);
          setIsTaskModalOpen(true);
        }}
        density="compact"
        userRole={user?.role}
      />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mes-taches-pagination">
              <button
                className="mes-taches-page-btn"
                onClick={() => setURLValue('page', String(currentPage - 1))}
                disabled={currentPage === 1}
                aria-label="Page précédente"
              >
                Précédent
              </button>
              
              <div className="mes-taches-page-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i + 1;
                  if (totalPages > 5) {
                    if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`mes-taches-page-num ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => setURLValue('page', String(pageNum))}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="mes-taches-page-btn"
                onClick={() => setURLValue('page', String(currentPage + 1))}
                disabled={currentPage === totalPages}
                aria-label="Page suivante"
              >
                Suivant
              </button>
              
              <select
                className="mes-taches-per-page"
                value={urlState.perPage}
                onChange={(e) => {
                  setURLValues({ perPage: e.target.value, page: '1' });
                }}
                aria-label="Nombre d'éléments par page"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(num => (
                  <option key={num} value={num}>
                    Afficher {num}
                  </option>
                ))}
              </select>
            </div>
          )}

      <ChatModal
        isOpen={!!selectedTaskForChat}
        onClose={() => {
          setSelectedTaskIdForChat(null);
          setTaskMessages([]);
        }}
        task={selectedTaskForChat ? {
          id: selectedTaskForChat.id,
          title: selectedTaskForChat.message,
          message: selectedTaskForChat.message,
          created_at: selectedTaskForChat.created_at,
          deadline: selectedTaskForChat.deadline,
          created_by_name: selectedTaskForChat.created_by_name,
          assigned_user_names: selectedTaskForChat.assigned_user_names,
          participants: selectedTaskForChat.assigned_user_ids?.map((userId, index) => ({
            id: userId,
            name: selectedTaskForChat.assigned_user_names?.[index] || 'Utilisateur',
            initials: selectedTaskForChat.assigned_user_names?.[index]?.split(' ').map(n => n[0]).join('').toUpperCase()
          }))
        } : null}
        currentUserId={user?.id || ''}
        messages={taskMessages}
        onSendMessage={handleSendMessage}
        onRefresh={loadTaskMessages}
        loading={messagesLoading}
      />

      {/* Floating Action Button for Mobile */}
      <button
        className="mes-taches-fab"
        onClick={() => {
          setEditingTask(null);
          setIsTaskModalOpen(true);
        }}
        aria-label="Nouvelle tâche"
      >
        <PlusIcon className="mes-taches-fab-icon" />
      </button>

      {/* Task Creation Modal */}
      <TaskCreationModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTask(null);
        }}
        onSuccess={() => {
          setIsTaskModalOpen(false);
          setEditingTask(null);
          fetchTasks();
          fetchStats();
          refetchUnreadCount();
        }}
        title={editingTask ? "Modifier la tâche" : "Créer une nouvelle tâche"}
        editingTask={editingTask}
        showDirectForm={true}
      />

      {/* Task Assignment Modal */}
      <TaskAssignmentModal
        isOpen={isAssignmentModalOpen}
        onClose={() => {
          setIsAssignmentModalOpen(false);
          setAssigningTask(null);
        }}
        task={assigningTask}
        users={users}
        onAssign={handleAssignment}
      />

      {/* Email Content Modal (for viewing emails linked to tasks) */}
      {viewingEmail && (
        <EmailContentModal
          email={viewingEmail}
          onClose={() => {
            setViewEmailId(null);
            setViewingEmail(null);
          }}
        />
      )}

      {/* Email to Affaire Modal (shown when completing task with email) */}
      {showEmailAffaireModal && emailForAffaire && (
        <EmailToAffaireModal
          isOpen={showEmailAffaireModal}
          email={emailForAffaire}
          onClose={() => {
            setShowEmailAffaireModal(false);
            setPendingDoneTask(null);
            setEmailForAffaire(null);
          }}
          onSuccess={handleEmailAffaireSuccess}
          onSkip={handleEmailAffaireSkip}
        />
      )}

      {/* Voicemail to Affaire Modal (validation d'une tâche issue d'un
          message vocal : lien affaire facultatif) */}
      {voicemailDoneTask && (
        <VoicemailToAffaireModal
          isOpen
          task={voicemailDoneTask}
          onClose={() => setVoicemailDoneTask(null)}
          onValidate={async (affaireId) => {
            const task = voicemailDoneTask;
            setVoicemailDoneTask(null);
            if (task) await completeMarkDone(task.id, affaireId);
          }}
        />
      )}
    </div>
  );
};